import { ArrowLeft, Info, Heart } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const HeartRateDetail = () => {
  const navigate = useNavigate();

  const weeklyData = [
    { day: "Mon", resting: 65 },
    { day: "Tue", resting: 63 },
    { day: "Wed", resting: 67 },
    { day: "Thu", resting: 64 },
    { day: "Fri", resting: 68 },
    { day: "Sat", resting: 62 },
    { day: "Sun", resting: 66 },
  ];

  const monthlyData = [
    { week: "Week 1", resting: 72 },
    { week: "Week 2", resting: 75 },
    { week: "Week 3", resting: 73 },
    { week: "Week 4", resting: 74 },
  ];

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            Resting Heart Rate
          </h1>
          <div className="w-10" />
        </div>

        {/* Current Heart Rate */}
        <Card className="from-chart-5/10 to-chart-5/5 rounded-2xl border-0 bg-gradient-to-br p-6 shadow-sm">
          <div className="text-center">
            <div className="bg-chart-5/20 mb-3 inline-flex h-20 w-20 items-center justify-center rounded-full">
              <Heart className="text-chart-5 h-10 w-10 animate-pulse" />
            </div>
            <p className="mb-2 text-sm text-muted-foreground">
              Current Heart Rate
            </p>
            <div className="mb-1 text-5xl font-bold text-foreground">68</div>
            <p className="text-sm text-muted-foreground">BPM</p>
          </div>
        </Card>

        {/* Heart Rate Zones */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-4 font-semibold text-foreground">
            Heart Rate Zones
          </h3>
          <div className="space-y-3">
            {[
              {
                zone: "Out of Range",
                range: "<50",
                color: "bg-chart-1",
                time: "18h 45m",
              },
              {
                zone: "Fat Burn",
                range: "70-120",
                color: "bg-chart-2",
                time: "3h 20m",
              },
              {
                zone: "Cardio",
                range: "120-150",
                color: "bg-chart-3",
                time: "1h 15m",
              },
              { zone: "Peak", range: "150+", color: "bg-chart-5", time: "20m" },
            ].map((zone, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`h-3 w-3 rounded-full ${zone.color}`} />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {zone.zone}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {zone.range} BPM
                    </p>
                  </div>
                </div>
                <span className="text-sm font-medium text-foreground">
                  {zone.time}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Tabs for Weekly/Monthly */}
        <Card className="rounded-2xl border-0 shadow-sm">
          <Tabs defaultValue="weekly" className="w-full">
            <TabsList className="m-4 grid w-full grid-cols-2 p-1">
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="weekly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={weeklyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="day"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="resting"
                    stroke="hsl(var(--chart-5))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--chart-5))", r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>

            <TabsContent value="monthly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={monthlyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="week"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="resting"
                    stroke="hsl(var(--chart-5))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--chart-5))", r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Information Card */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="bg-info-light flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl">
              <Info className="text-info h-5 w-5" />
            </div>
            <div>
              <h3 className="mb-2 font-semibold text-foreground">
                Understanding Heart Rate
              </h3>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Your heart rate is measured by optical sensors on your Fitbit
                that detect blood flow through your wrist. Resting heart rate
                (RHR) is measured when you're inactive and indicates overall
                cardiovascular fitness. A lower RHR typically indicates better
                fitness. Heart rate variability (HRV) measures the variation in
                time between heartbeats and is an indicator of stress and
                recovery.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default HeartRateDetail;
